import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import API_BASE from '../apiBase';
import { Home, Search, Users, ArrowUpDown, Ban, CheckCircle2, Trash2, X } from 'lucide-react';

// realtime helpers
import useAutoRefresh from './useAutoRefresh';
import { useListenInvalidation, broadcastInvalidation } from './adminInvalidation';

function Badge({ active }) {
  return (
    <span
      className={
        'px-2 py-1 rounded-full text-xs border ' +
        (active
          ? 'bg-green-50 text-green-700 border-green-100'
          : 'bg-gray-100 text-gray-600 border-gray-200')
      }
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
}

export default function AdminUsersPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all'); // all | active | inactive
  const [sort, setSort] = useState('created'); // created | email | last_login | role | name | status
  const [dir, setDir] = useState('desc');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ all: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // modal state (delete confirm)
  const [confirm, setConfirm] = useState(null); // { id, email }

  const reqId = useRef(0);

  const load = useCallback(async () => {
    const myId = ++reqId.current;
    setLoading(true);
    setErr('');
    try {
      const params = new URLSearchParams({
        q: q.trim(),
        status,
        sort,
        dir,
        limit: '200',
      });
      const r = await fetch(`${API_BASE}/api/admin/users?` + params.toString(), {
        credentials: 'include',
        cache: 'no-store',
      });
      const j = await r.json();
      if (reqId.current !== myId) return;
      if (r.ok) {
        setRows(j.items || []);
        setCounts(j.counts || { all: 0, active: 0, inactive: 0 });
      } else {
        setRows([]);
        setCounts({ all: 0, active: 0, inactive: 0 });
        setErr(j.error || 'Failed to load users');
      }
    } catch {
      if (reqId.current === myId) setErr('Network error');
    } finally {
      if (reqId.current === myId) setLoading(false);
    }
  }, [q, status, sort, dir]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  useAutoRefresh(load, { intervalMs: 30000, refetchOnFocus: true });
  useListenInvalidation(() => load());

  function toggleSort(col) {
    if (sort === col) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(col);
      setDir(col === 'created' ? 'desc' : 'asc');
    }
  }

  async function toggleActive(u) {
    // guard: cannot touch admins
    if (String(u.role).toLowerCase() === 'admin') return;

    try {
      setErr('');
      const r = await fetch(`${API_BASE}/api/admin/users/${u.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Update failed');
      broadcastInvalidation('users:update', { id: u.id, is_active: !u.is_active });
      // quick local refresh
      setRows((rows) =>
        rows.map((row) => (row.id === u.id ? { ...row, is_active: !u.is_active } : row))
      );
    } catch (e) {
      setErr(e.message || 'Update failed');
    }
  }

  async function confirmDelete() {
    if (!confirm) return;
    try {
      setErr('');
      const r = await fetch(`${API_BASE}/api/admin/users/${confirm.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Delete failed');
      broadcastInvalidation('users:delete', { id: confirm.id });
      setConfirm(null);
      // reload lists/counters
      load();
    } catch (e) {
      setErr(e.message || 'Delete failed');
    }
  }

  const headerBtn = (label, col) => (
    <button
      onClick={() => toggleSort(col)}
      className={`inline-flex items-center gap-1 ${
        sort === col ? 'text-orange-700' : 'text-gray-600'
      } hover:text-orange-700`}
      title={`Sort by ${label}`}
    >
      {label}
      <ArrowUpDown className="w-4 h-4" />
    </button>
  );

  const totalText = useMemo(() => {
    const subset =
      status === 'active' ? counts.active : status === 'inactive' ? counts.inactive : counts.all;
    return `${subset} user${subset === 1 ? '' : 's'}`;
  }, [counts, status]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-orange-600" />
            Users
          </h1>

          <div className="flex items-center gap-2">
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>

            {/* Status filter */}
            <div className="flex items-center rounded-lg border border-gray-300 bg-white overflow-hidden">
              {['all', 'active', 'inactive'].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-3 py-2 text-sm ${
                    status === s ? 'bg-orange-600 text-white' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {s[0].toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>

            {/* Search */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                load();
              }}
              className="relative"
            >
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search email, name…"
                className="pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent w-72"
              />
            </form>
          </div>
        </div>

        {err && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-800 border border-red-100">
            {err}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="text-sm text-gray-600">{loading ? 'Loading…' : totalText}</div>
            <div className="text-xs text-gray-400">
              All: {counts.all} • Active: {counts.active} • Inactive: {counts.inactive}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-600">
                  <th className="py-3 px-4">{headerBtn('Email', 'email')}</th>
                  <th className="py-3 px-4">{headerBtn('Name', 'name')}</th>
                  <th className="py-3 px-4">{headerBtn('Role', 'role')}</th>
                  <th className="py-3 px-4">{headerBtn('Status', 'status')}</th>
                  <th className="py-3 px-4">{headerBtn('Registered', 'created')}</th>
                  <th className="py-3 px-4">{headerBtn('Last Login', 'last_login')}</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="py-6 px-4 text-gray-500" colSpan={7}>
                      Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="py-6 px-4 text-gray-500" colSpan={7}>
                      No users found.
                    </td>
                  </tr>
                ) : (
                  rows.map((u) => {
                    const isAdmin = String(u.role).toLowerCase() === 'admin';
                    return (
                      <tr key={u.id} className="border-t last:border-b-0">
                        <td className="py-3 px-4 font-medium text-gray-900">{u.email}</td>
                        <td className="py-3 px-4 text-gray-700">
                          {u.first_name || u.last_name
                            ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                            : '—'}
                        </td>
                        <td className="py-3 px-4 text-gray-700">{u.role || 'user'}</td>
                        <td className="py-3 px-4">
                          <Badge active={!!u.is_active} />
                        </td>
                        <td className="py-3 px-4 text-gray-600">{fmtDate(u.created_at)}</td>
                        <td className="py-3 px-4 text-gray-600">{fmtDate(u.last_login_at)}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleActive(u)}
                              disabled={isAdmin}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${
                                u.is_active
                                  ? 'border-yellow-200 text-yellow-700 hover:bg-yellow-50'
                                  : 'border-green-200 text-green-700 hover:bg-green-50'
                              } ${isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={
                                isAdmin
                                  ? 'Admins cannot be deactivated'
                                  : u.is_active
                                  ? 'Deactivate'
                                  : 'Activate'
                              }
                            >
                              {u.is_active ? (
                                <>
                                  <Ban className="w-4 h-4" /> Deactivate
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className="w-4 h-4" /> Activate
                                </>
                              )}
                            </button>

                            <button
                              onClick={() => !isAdmin && setConfirm({ id: u.id, email: u.email })}
                              disabled={isAdmin}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 ${
                                isAdmin ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              title={isAdmin ? 'Admins cannot be deleted' : 'Delete user'}
                            >
                              <Trash2 className="w-4 h-4" /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete confirm modal */}
      {confirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="font-semibold">Delete user</h3>
              <button className="p-1 text-gray-500 hover:text-gray-700" onClick={() => setConfirm(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p>
                Are you sure you want to permanently delete{' '}
                <span className="font-semibold">{confirm.email}</span>? This will remove their wishlists.
                Orders will remain but no longer be linked to this user.
              </p>
              {err && <div className="text-sm text-red-600">{err}</div>}
            </div>
            <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
              <button
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
